import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { MultiComboBox } from "@itandi/itandi-bb-ui/MultiComboBox";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import type { ComponentProps, FC } from "react";
import { useCallback, useMemo, useState } from "react";
import { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import { useVendorPartnerCandidates } from "~/hooks/vendors/useVendorPartnerCandidates";
import { isSuccess } from "~/utils/api/api_handler";
import { create } from "~/utils/api/vendors/VendorPartner";
import { generatePath } from "~/utils/generatePath";

type Props = DeepReadonly<{
	handleClose: () => void;
}>;

type MultiComboBoxOption = Required<
	ComponentProps<typeof MultiComboBox>
>["value"];

export const VendorPartnerAddModal: FC<Props> = ({ handleClose }: Props) => {
	const { notify } = useNotification();
	const [selectedValues, setSelectedValues] = useState<MultiComboBoxOption>([]);
	const { mutate: globalMutate } = useSWRConfig();
	const { data: vendorCompanyCandidates } =
		useVendorPartnerCandidates().index("");

	const vendorOptions = useMemo(() => {
		return (
			vendorCompanyCandidates?.map((vendorCompany) => ({
				label: vendorCompany.name,
				value: String(vendorCompany.id)
			})) ?? []
		);
	}, [vendorCompanyCandidates]);

	const { trigger, isMutating } = useSWRMutation(
		generatePath("/vendors/vendor_partners"),
		async (_key, { arg }: { arg: { selectedIds: number[] } }) => {
			return await create(arg.selectedIds);
		}
	);

	const handleSubmit = useCallback(async () => {
		const response = await trigger({
			selectedIds: selectedValues?.map((opt) => Number(opt.value))
		});
		if (isSuccess(response)) {
			await globalMutate(generatePath("/vendors/vendor_partners"));
			notify({ content: "協力会社を追加しました。", variant: "success" });
			handleClose();
		} else {
			notify({ content: "エラーが発生しました。", variant: "error" });
		}
	}, [trigger, selectedValues, globalMutate, notify, handleClose]);

	return (
		<Modalv2 isOpen onClose={handleClose}>
			<ModalHeader title="協力会社の追加" />
			<ModalBody>
				<FormGroup variant="vertical">
					<FormControl label="追加する協力会社">
						<MultiComboBox
							onChange={setSelectedValues}
							options={vendorOptions}
							placeholder="協力会社を検索"
							value={selectedValues}
						/>
					</FormControl>
				</FormGroup>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button
						disabled={isMutating}
						onClick={handleClose}
						variant="secondary"
					>
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						disabled={!selectedValues || selectedValues.length === 0}
						loading={isMutating}
						onClick={handleSubmit}
						variant="primary"
					>
						追加
					</Button>
				}
			/>
		</Modalv2>
	);
};
